import { useSlotCapacity } from '@/features/reservations/hooks/useReservations';
import { useReservationConfig } from '@/features/reservation-setup/hooks/useReservationConfig';
import { useT } from '@/i18n';
import { f7 } from 'framework7-react';

export function useBookingLimits(
  date: string,
  time: string,
  partySize: number,
  slotChanged = true,
) {
  const tr = useT().createReservation;
  // Reservation config drives the booking-window and party-size limits so the
  // form mirrors the same rules the server enforces. Falls back to permissive
  // defaults while the config is still loading.
  const { data: rsvConfig } = useReservationConfig();
  const maxPartySize = rsvConfig?.maxPartySize ?? 20;
  // Live capacity probe for the chosen slot. When the slot is already at its
  // configured limit we don't block staff (they may need to overbook a phone
  // call), but we warn inline and confirm on submit so it's a conscious choice.
  const { data: slotCapacity } = useSlotCapacity(date, time, partySize);
  // A party over the configured limit already exceeds the per-slot cap, so both
  // the party-size and slot-full checks fire at once. The party-size notice is
  // the specific one - let it win and suppress the generic slot-full notice, so
  // only ever a single warning (and single confirm) shows.
  const partyOverLimit = partySize > maxPartySize;
  const slotFull = slotChanged && !!slotCapacity && !slotCapacity.available;

  // A single guard, never both: an over-limit party already implies a full
  // slot, so we only confirm the party size in that case. Either way staff
  // can still overbook - the confirm just makes it a conscious choice.
  const confirmThen = (submit: () => void, askPartySize = partyOverLimit) => {
    if (askPartySize) {
      f7.dialog.confirm(
        tr.partySizeOverLimitConfirm(maxPartySize),
        tr.partySizeOverLimitTitle,
        submit,
      );
    } else if (slotFull) {
      f7.dialog.confirm(tr.slotFullConfirm, tr.slotFullTitle, submit);
    } else {
      submit();
    }
  };

  return { maxPartySize, partyOverLimit, showSlotFull: slotFull && !partyOverLimit, confirmThen };
}
